// src/components/ImageUploader.jsx
import React, { useState, useRef } from "react";
import { uploadImage, deleteImage } from "../services/azureBlobService";

const ImageUploader = ({
  projectId,
  kategorie,
  images = [],
  onImagesChange,
  maxBilder = 10,
}) => {
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [error, setError] = useState(null);
  const [uploadProgress, setUploadProgress] = useState("");
  const fileInputRef = useRef(null);

  const handleFileSelect = async (files) => {
    if (!files || files.length === 0) return;

    setError(null);
    setUploadProgress("");

    if (images.length + files.length > maxBilder) {
      setError(
        `Maximal ${maxBilder} Dateien pro Kategorie erlaubt. Du hast bereits ${images.length} Dateien hochgeladen.`
      );
      return;
    }

    setUploading(true);

    try {
      const uploadPromises = Array.from(files).map(async (file, index) => {
        try {
          setUploadProgress(
            `Lade Datei ${index + 1} von ${files.length} hoch...`
          );
          const result = await uploadImage(
            projectId,
            kategorie,
            file,
            "",
            false
          );
          return result;
        } catch (error) {
          console.error(`Upload von ${file.name} fehlgeschlagen:`, error);
          throw new Error(`${file.name}: ${error.message}`);
        }
      });

      const newImages = await Promise.all(uploadPromises);
      onImagesChange([...images, ...newImages]);
      setUploadProgress("");

      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    } catch (error) {
      console.error("Upload failed:", error);
      setError(error.message);
    } finally {
      setUploading(false);
      setUploadProgress("");
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleFileSelect(e.dataTransfer.files);
  };

  const handleDelete = async (bildId) => {
    if (!confirm("Datei wirklich löschen?")) return;

    setError(null);

    try {
      await deleteImage(projectId, kategorie, bildId);
      onImagesChange(images.filter((img) => img.bildId !== bildId));
    } catch (error) {
      console.error("Delete failed:", error);
      setError(`Löschen fehlgeschlagen: ${error.message}`);
    }
  };

  const handleUpdateText = (bildId, bildtext) => {
    const updatedImages = images.map((img) =>
      img.bildId === bildId ? { ...img, bildtext } : img
    );
    onImagesChange(updatedImages);
  };

  const handleUpdatePublic = (bildId, isPublic) => {
    const updatedImages = images.map((img) =>
      img.bildId === bildId ? { ...img, isPublic } : img
    );
    onImagesChange(updatedImages);
  };

  return (
    <div className="image-uploader">
      <div className="uploader-header">
        <h4>{kategorie}</h4>
        <span className="image-count">
          {images.length} / {maxBilder}
        </span>
      </div>

      {error && (
        <div className="upload-error">
          <strong>Fehler:</strong> {error}
          <button
            type="button"
            className="error-close"
            onClick={() => setError(null)}
          >
            ×
          </button>
        </div>
      )}

      {uploadProgress && (
        <div className="upload-progress">{uploadProgress}</div>
      )}

      <div
        className={`upload-area ${dragOver ? "drag-over" : ""} ${
          uploading ? "uploading" : ""
        }`}
        onDrop={handleDrop}
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept="image/jpeg,image/jpg,image/png,image/gif,image/webp,video/mp4,video/quicktime,video/webm"
          onChange={(e) => handleFileSelect(e.target.files)}
          style={{ display: "none" }}
        />
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading || images.length >= maxBilder}
          className="upload-button"
        >
          {uploading ? "Hochladen..." : "Bilder oder Videos auswählen"}
        </button>
        <p className="upload-info">
          oder Bilder/Videos hierher ziehen (JPG, PNG, GIF, WebP, MP4, MOV, max.
          10MB)
        </p>
      </div>

      {images.length > 0 && (
        <div className="images-list">
          {images.map((image) => {
            // Debug: URLs ausgeben
            console.log("Rendering image:", image.bildId, "URLs:", image.urls);

            // Prüfe welche URL verfügbar ist
            const imageUrl =
              image.urls?.thumbnail || image.urls?.medium || image.urls?.small;

            return (
              <div key={image.bildId} className="image-item">
                <div className="image-thumbnail">
                  {imageUrl ? (
                    <img
                      src={imageUrl}
                      alt={image.bildtext || "Projektbild"}
                      onLoad={() => console.log("Datei geladen:", imageUrl)}
                      onError={(e) => {
                        console.error("Datei-Ladefehler:", imageUrl, image);
                        e.target.src =
                          "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTIwIiBoZWlnaHQ9IjEyMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTIwIiBoZWlnaHQ9IjEyMCIgZmlsbD0iI2VlZSIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTQiIGZpbGw9IiM5OTkiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5CaWxkIGZlaGx0PC90ZXh0Pjwvc3ZnPg==";
                      }}
                    />
                  ) : (
                    <div
                      style={{
                        padding: "20px",
                        textAlign: "center",
                        color: "#999",
                      }}
                    >
                      Keine URL
                    </div>
                  )}
                </div>

                <div className="image-details">
                  <input
                    type="text"
                    value={image.bildtext}
                    onChange={(e) =>
                      handleUpdateText(image.bildId, e.target.value)
                    }
                    placeholder="Beschrieb"
                    className="image-text-input"
                  />

                  <div className="image-checkbox">
                    <input
                      type="checkbox"
                      id={`public-${image.bildId}`}
                      checked={image.isPublic}
                      onChange={(e) =>
                        handleUpdatePublic(image.bildId, e.target.checked)
                      }
                    />
                    <label htmlFor={`public-${image.bildId}`}>Öffentlich</label>
                  </div>

                  <div className="image-metadata">
                    <span>{image.originalFileName}</span>
                    <span>{(image.fileSize / 1024).toFixed(1)} KB</span>
                  </div>
                </div>

                <div className="image-actions">
                  <button
                    type="button"
                    onClick={() => handleDelete(image.bildId)}
                    className="delete-button"
                  >
                    Löschen
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ImageUploader;
